import { useDismiss, usePopoverPosition } from "@rothoblaas/design-system";
import { CSSProperties, RefObject, useRef } from "react";

// Kept between a dropdown menu and the window's edges, as usePopoverPosition's own default.
const VIEWPORT_MARGIN_PX = 16;
// Between the trigger and the menu.
const TRIGGER_GAP_PX = 6;

export type DropdownMenu<M extends HTMLElement = HTMLUListElement> = {
  /** On the element the menu opens from: it's positioned against it, and a click on it doesn't dismiss the menu. */
  triggerRef: RefObject<HTMLDivElement | null>;
  /** On the menu: a list by default, another element given as M (e.g. a div holding a ds2 AccountMenu). */
  menuRef: RefObject<M | null>;
  /** The menu's position, fixed in the window: render the menu portaled to the body with it. */
  menuStyle: CSSProperties;
};

/**
 * A dropdown menu that's never drawn outside the window, e.g. the PDF buttons' language menus: under
 * its trigger and right-aligned with it, or above it / along its other edge when it would overflow
 * (usePopoverPosition). When neither side has room (a short window) it's clamped inside anyway, so
 * give the menu max sizes of the window's minus 32px, scrolling past them. At least as wide as its
 * trigger. Closed on a click outside, Escape or a scroll, as the design-system ContextMenu is.
 */
const useDropdownMenu = <M extends HTMLElement = HTMLUListElement>(
  isOpen: boolean,
  onClose: () => void,
): DropdownMenu<M> => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<M>(null);
  const position = usePopoverPosition({
    isOpen,
    triggerRef,
    panelRef: menuRef,
    align: "end",
    gap: TRIGGER_GAP_PX,
    margin: VIEWPORT_MARGIN_PX,
    onScrollBehavior: "close",
    onScrollClose: onClose,
  });
  useDismiss(isOpen, onClose, [triggerRef, menuRef]);

  const menu = menuRef.current;
  if (!position || !menu) {
    // Measured first, out of sight.
    return { triggerRef, menuRef, menuStyle: { position: "fixed", top: 0, left: 0, visibility: "hidden" } };
  }
  const clamp = (value: number, size: number, viewport: number) =>
    Math.max(VIEWPORT_MARGIN_PX, Math.min(value, viewport - VIEWPORT_MARGIN_PX - size));
  return {
    triggerRef,
    menuRef,
    menuStyle: {
      position: "fixed",
      top: clamp(Number(position.top), menu.offsetHeight, window.innerHeight),
      left: clamp(Number(position.left), menu.offsetWidth, window.innerWidth),
      minWidth: triggerRef.current?.offsetWidth,
    },
  };
};

export default useDropdownMenu;
